import { useState, useEffect, useRef, useCallback } from 'react'
import { useAuth, useClerk } from '@clerk/react'

const STORAGE_KEY = 'renteasii_last_activity'

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'click', 'scroll', 'touchstart', 'wheel']

const readSharedActivity = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    return raw ? Number(raw) : null
  } catch {
    return null
  }
}

const writeSharedActivity = (ts) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(ts))
  } catch {
    // localStorage unavailable; per-tab clock only
  }
}

const clearSignedInLocalStorage = () => {
  try {
    window.localStorage.removeItem('userRentEasy')
    window.localStorage.removeItem('agentProfilePic')
    window.localStorage.removeItem('AgentId_Entered')
  } catch {
    // noop
  }
}

export const useIdleSignOut = ({ idleMinutes = 60, warningSeconds = 60 } = {}) => {
  const { isLoaded, isSignedIn } = useAuth()
  const clerk = useClerk()

  const [status, setStatus] = useState('active')
  const [secondsLeft, setSecondsLeft] = useState(warningSeconds)

  const idleMs = idleMinutes * 60 * 1000

  const statusRef = useRef('active')
  const secondsLeftRef = useRef(warningSeconds)
  const warningStartedAtRef = useRef(null)
  const lastActivityRef = useRef(null)
  const signedOutRef = useRef(false)

  const setStatusBoth = useCallback((next) => {
    statusRef.current = next
    setStatus(next)
  }, [])

  const updateSeconds = useCallback((v) => {
    const val = Math.max(0, Math.floor(v))
    secondsLeftRef.current = val
    setSecondsLeft(val)
  }, [])

  const performSignOut = useCallback(() => {
    if (signedOutRef.current) return
    signedOutRef.current = true
    clearSignedInLocalStorage()
    clerk.signOut({ redirectUrl: process.env.REACT_APP_CLERK_SIGN_IN_URL || '/' }).catch(() => {})
  }, [clerk])

  const resetTimer = useCallback(() => {
    const now = Date.now()
    lastActivityRef.current = now
    writeSharedActivity(now)
    warningStartedAtRef.current = null
    updateSeconds(warningSeconds)
    setStatusBoth('active')
  }, [updateSeconds, warningSeconds, setStatusBoth])

  const onActivity = useCallback(() => {
    const now = Date.now()
    lastActivityRef.current = now
    writeSharedActivity(now)
    if (statusRef.current === 'warning') {
      warningStartedAtRef.current = null
      updateSeconds(warningSeconds)
      setStatusBoth('active')
    }
  }, [updateSeconds, warningSeconds, setStatusBoth])

  const evaluate = useCallback(() => {
    if (!isLoaded || !isSignedIn || signedOutRef.current) return

    const now = Date.now()
    const shared = readSharedActivity()
    const last = shared ?? lastActivityRef.current ?? now
    lastActivityRef.current = last

    if (statusRef.current === 'active') {
      if (now - last >= idleMs) {
        warningStartedAtRef.current = now
        updateSeconds(warningSeconds)
        setStatusBoth('warning')
      }
    } else {
      const sinceStart = warningStartedAtRef.current ?? now
      const remainingMs = warningSeconds * 1000 - (now - sinceStart)
      if (remainingMs <= 0) {
        performSignOut()
        return
      }
      updateSeconds(remainingMs / 1000)
    }
  }, [isLoaded, isSignedIn, idleMs, warningSeconds, performSignOut, updateSeconds, setStatusBoth])

  const onVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'visible') evaluate()
  }, [evaluate])

  const onStorage = useCallback((e) => {
    if (e.key !== STORAGE_KEY) return
    const value = Number(e.newValue)
    if (!Number.isFinite(value)) return
    if (value >= (lastActivityRef.current ?? 0)) {
      lastActivityRef.current = value
      if (statusRef.current === 'warning') {
        warningStartedAtRef.current = null
        updateSeconds(warningSeconds)
        setStatusBoth('active')
      }
    }
  }, [updateSeconds, warningSeconds, setStatusBoth])

  useEffect(() => {
    if (!isSignedIn) {
      warningStartedAtRef.current = null
      updateSeconds(warningSeconds)
      setStatusBoth('active')
    }
  }, [isSignedIn, updateSeconds, warningSeconds, setStatusBoth])

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return

    lastActivityRef.current = readSharedActivity() ?? Date.now()
    writeSharedActivity(lastActivityRef.current)

    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, onActivity, { passive: true }))
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('storage', onStorage)

    const intervalId = window.setInterval(evaluate, 1000)

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, onActivity))
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('storage', onStorage)
      window.clearInterval(intervalId)
    }
  }, [isLoaded, isSignedIn, onActivity, onVisibilityChange, onStorage, evaluate])

  return { status, secondsLeft, resetTimer }
}

export default useIdleSignOut